import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { api } from '../App';
import { useParams } from 'react-router-dom';
import { Plus, Shield, ShieldOff, Trash2, RefreshCw, Settings, Edit2, Check, X, Filter, ArrowUpDown, History, RotateCcw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const Portfolio = ({ user, setUser }) => {
  const { memberId } = useParams();
  const [member, setMember] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [lasAccounts, setLasAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [changeHistory, setChangeHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'symbol', direction: 'asc' });
  const [filterTag, setFilterTag] = useState('all');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState({ type: '', data: null, message: '' });
  const [formData, setFormData] = useState({
    symbol: '',
    quantity: 0,
    purchase_type: 'Legacy',
    purchase_date: new Date().toISOString().split('T')[0],
    purchase_price: 0,
    grandfathered: false,
    grandfathered_price: 0,
    grandfathered_date: '',
    bank_name: '',
    quality_tag: 'New',
  });

  const qualityTags = ['New', 'Moderate', 'Low', 'High', 'V Low', 'VV Low'];

  useEffect(() => {
    fetchData();
  }, [memberId]);

  const fetchData = async () => {
    try {
      const [memberRes, holdingsRes, lasRes] = await Promise.all([
        api.get(`/family-members/${memberId}`),
        api.get(`/holdings?member_id=${memberId}`),
        api.get(`/las-accounts?member_id=${memberId}`),
      ]);
      setMember(memberRes.data);
      setHoldings(holdingsRes.data);
      setLasAccounts(lasRes.data);
    } catch (err) {
      toast.error('Failed to load portfolio');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get('/change-history?entity_type=holding&limit=50');
      setChangeHistory(res.data);
    } catch (err) {
      toast.error('Failed to load history');
    } finally {
      setLoadingHistory(false);
    }
  };

  // Confirmation dialog handlers
  const showConfirm = (type, data, message) => {
    setConfirmAction({ type, data, message });
    setShowConfirmDialog(true);
  };

  const handleConfirm = async () => {
    const { type, data } = confirmAction;
    setShowConfirmDialog(false);
    
    if (type === 'rollback') {
      try {
        const res = await api.post(`/change-history/${data}/rollback`);
        toast.success(res.data.message);
        fetchHistory();
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to rollback');
      }
    } else if (type === 'delete') {
      try {
        await api.delete(`/holdings/${data}`);
        toast.success('Holding deleted');
        fetchData();
      } catch (err) {
        toast.error('Failed to delete holding');
      }
    }
  };

  const handleRollback = (historyId, description) => {
    showConfirm('rollback', historyId, `Are you sure you want to rollback this change?\n\n${description || ''}`);
  };

  const openHistoryModal = () => {
    fetchHistory();
    setHistoryOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/holdings', {
        ...formData,
        member_id: memberId,
        purchase_date: new Date(formData.purchase_date).toISOString(),
        grandfathered_date: formData.grandfathered_date
          ? new Date(formData.grandfathered_date).toISOString()
          : null,
        custom_fields: {
          category: formData.quality_tag
        }
      });
      toast.success('Holding added successfully');
      setOpen(false);
      setFormData({
        symbol: '',
        quantity: 0,
        purchase_type: 'Legacy',
        purchase_date: new Date().toISOString().split('T')[0],
        purchase_price: 0,
        grandfathered: false,
        grandfathered_price: 0,
        grandfathered_date: '',
        bank_name: '',
        quality_tag: 'New',
      });
      fetchData();
    } catch (err) {
      toast.error('Failed to add holding');
    }
  };

  const handleEdit = (holding) => {
    setEditingId(holding.id);
    setEditFormData({
      symbol: holding.symbol,
      quantity: holding.quantity,
      purchase_type: holding.purchase_type,
      purchase_price: holding.purchase_price,
      quality_tag: holding.custom_fields?.category || 'Unknown',
    });
  };

  const handleSaveEdit = async (id) => {
    try {
      await api.put(`/holdings/${id}`, {
        ...editFormData,
        custom_fields: {
          category: editFormData.quality_tag
        }
      });
      toast.success('Holding updated');
      setEditingId(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to update holding');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFormData({});
  };

  const togglePledge = async (holdingId, currentStatus) => {
    if (!currentStatus && lasAccounts.length === 0) {
      toast.error('Please create a LAS account first');
      return;
    }
    try {
      await api.patch(`/holdings/${holdingId}/pledge`, null, {
        params: { las_account_id: currentStatus ? null : lasAccounts[0]?.id },
      });
      toast.success(currentStatus ? 'Unpledged successfully' : 'Pledged successfully');
      fetchData();
    } catch (err) {
      toast.error('Failed to update pledge status');
    }
  };

  const handleDelete = (id, symbol) => {
    showConfirm('delete', id, `Are you sure you want to delete the holding "${symbol}"?\n\nThis action will be recorded in history and can be rolled back.`);
  };

  const [prices, setPrices] = useState({});
  const [loadingPrices, setLoadingPrices] = useState(false);

  const fetchPrices = async () => {
    setLoadingPrices(true);
    const newPrices = {};
    for (const holding of holdings) {
      try {
        const res = await api.get(`/stock-price/${holding.symbol}`);
        newPrices[holding.symbol] = res.data.price;
      } catch (err) {
        newPrices[holding.symbol] = holding.purchase_price;
      }
    }
    setPrices(newPrices);
    setLoadingPrices(false);
  };

  useEffect(() => {
    if (holdings.length > 0) {
      fetchPrices();
    }
  }, [holdings.length]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getFilteredAndSortedHoldings = () => {
    let filtered = holdings;
    
    // Filter by quality tag
    if (filterTag !== 'all') {
      filtered = holdings.filter(h => (h.custom_fields?.category || 'Unknown') === filterTag);
    }
    
    // Sort
    return filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch(sortConfig.key) {
        case 'symbol':
          return sortConfig.direction === 'asc' 
            ? a.symbol.localeCompare(b.symbol)
            : b.symbol.localeCompare(a.symbol);
        case 'quantity':
          aVal = a.quantity;
          bVal = b.quantity;
          break;
        case 'price':
          aVal = a.purchase_price;
          bVal = b.purchase_price;
          break;
        case 'value':
          aVal = (prices[a.symbol] || a.purchase_price) * a.quantity;
          bVal = (prices[b.symbol] || b.purchase_price) * b.quantity;
          break;
        case 'type':
          return sortConfig.direction === 'asc'
            ? (a.purchase_type || '').localeCompare(b.purchase_type || '')
            : (b.purchase_type || '').localeCompare(a.purchase_type || '');
        case 'status':
          aVal = a.pledged ? 1 : 0;
          bVal = b.pledged ? 1 : 0;
          break;
        case 'tag':
          return sortConfig.direction === 'asc'
            ? (a.custom_fields?.category || '').localeCompare(b.custom_fields?.category || '')
            : (b.custom_fields?.category || '').localeCompare(a.custom_fields?.category || '');
        default:
          aVal = a.symbol;
          bVal = b.symbol;
          return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  const renderSortHeader = (key, label) => (
    <th 
      className="text-left py-3 px-4 font-semibold text-sm uppercase tracking-wider text-gray-600 cursor-pointer hover:bg-gray-100"
      onClick={() => handleSort(key)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortConfig.key === key ? (
          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
        ) : (
          <ArrowUpDown size={12} className="text-gray-400" />
        )}
      </div>
    </th>
  );

  const getQualityTagColor = (tag) => {
    const colors = {
      'New': 'bg-green-100 text-green-800',
      'High': 'bg-blue-100 text-blue-800',
      'Moderate': 'bg-yellow-100 text-yellow-800',
      'Low': 'bg-orange-100 text-orange-800',
      'V Low': 'bg-red-100 text-red-800',
      'VV Low': 'bg-red-200 text-red-900',
      'Unknown': 'bg-gray-100 text-gray-800',
    };
    return colors[tag] || colors['Unknown'];
  };

  // Get unique tags from holdings for filter dropdown
  const availableTags = ['all', ...new Set(holdings.map(h => h.custom_fields?.category || 'Unknown'))];

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="text-center py-20">Loading...</div>
      </Layout>
    );
  }

  const displayHoldings = getFilteredAndSortedHoldings();
  const totalValue = holdings.reduce((sum, h) => sum + (prices[h.symbol] || h.purchase_price) * h.quantity, 0);
  const pledgedValue = holdings
    .filter((h) => h.pledged)
    .reduce((sum, h) => sum + (prices[h.symbol] || h.purchase_price) * h.quantity, 0);

  return (
    <Layout user={user} setUser={setUser}>
      <div data-testid="portfolio-page">
        {/* Change History Dialog */}
        <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History size={20} />
                Change History
              </DialogTitle>
            </DialogHeader>
            {loadingHistory ? (
              <div className="text-center py-8">Loading history...</div>
            ) : changeHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No changes recorded yet. Changes made in Edit Mode will appear here.
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 mb-4">
                  Recent changes to holdings. Click Rollback to undo a change.
                </p>
                {changeHistory.map((item) => (
                  <div key={item.id} className={`p-4 rounded-lg border ${item.action === 'delete' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${item.action === 'delete' ? 'bg-red-200 text-red-800' : 'bg-blue-200 text-blue-800'}`}>
                            {item.action.toUpperCase()}
                          </span>
                          <span className="text-sm text-gray-500">
                            {new Date(item.created_at).toLocaleString('en-IN')}
                          </span>
                        </div>
                        <p className="font-medium">{item.description}</p>
                        <div className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">Previous:</span>{' '}
                          {item.previous_data?.symbol} × {item.previous_data?.quantity} @ ₹{item.previous_data?.purchase_price}
                          {item.previous_data?.custom_fields?.category && (
                            <span className="ml-2 text-xs bg-gray-200 px-1 rounded">{item.previous_data.custom_fields.category}</span>
                          )}
                        </div>
                        {item.action === 'update' && item.new_data && (
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Changed to:</span>{' '}
                            {Object.entries(item.new_data).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(', ')}
                          </div>
                        )}
                      </div>
                      <Button
                        onClick={() => handleRollback(item.id, item.description)}
                        className="btn-outline flex items-center gap-1 text-sm"
                        size="sm"
                      >
                        <RotateCcw size={14} />
                        Rollback
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <div className="flex flex-col gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight mb-1 sm:mb-2">{member?.name}&apos;s Portfolio</h1>
            <p className="text-sm sm:text-base text-gray-600">{member?.relation} • {holdings.length} holdings</p>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Button
              onClick={openHistoryModal}
              size="sm"
              className="btn-outline flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
            >
              <History size={16} />
              <span className="hidden sm:inline">History</span>
            </Button>
            <Button
              onClick={() => setEditMode(!editMode)}
              size="sm"
              className={`btn-outline flex items-center gap-1 sm:gap-2 text-xs sm:text-sm ${editMode ? 'bg-amber-50 border-amber-500 text-amber-700' : ''}`}
            >
              <Settings size={16} />
              <span className="hidden sm:inline">{editMode ? 'Exit Edit' : 'Edit Mode'}</span>
              <span className="sm:hidden">{editMode ? 'Exit' : 'Edit'}</span>
            </Button>
            <Button
              data-testid="refresh-prices-button"
              onClick={fetchPrices}
              disabled={loadingPrices}
              size="sm"
              className="btn-secondary flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
            >
              <RefreshCw size={16} className={loadingPrices ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Refresh Prices</span>
              <span className="sm:hidden">Refresh</span>
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-holding-button" size="sm" className="btn-primary flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                  <Plus size={16} />
                  <span className="hidden sm:inline">Add Holding</span>
                  <span className="sm:hidden">Add</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add Holding</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Symbol *</label>
                    <input
                      type="text"
                      data-testid="holding-symbol-input"
                      className="input-field w-full"
                      placeholder="e.g., RELIANCE, TCS"
                      value={formData.symbol}
                      onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Quantity *</label>
                      <input
                        type="number"
                        data-testid="holding-quantity-input"
                        className="input-field w-full"
                        value={formData.quantity}
                        onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) })}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Purchase Price *</label>
                      <input
                        type="number"
                        data-testid="holding-price-input"
                        step="0.01"
                        className="input-field w-full"
                        value={formData.purchase_price}
                        onChange={(e) => setFormData({ ...formData, purchase_price: parseFloat(e.target.value) })}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Purchase Type *</label>
                      <select
                        data-testid="holding-type-select"
                        className="input-field w-full"
                        value={formData.purchase_type}
                        onChange={(e) => setFormData({ ...formData, purchase_type: e.target.value })}
                      >
                        <option value="Legacy">Legacy</option>
                        <option value="Primary">Primary Market</option>
                        <option value="MTF">MTF</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Quality Tag *</label>
                      <select
                        className="input-field w-full"
                        value={formData.quality_tag}
                        onChange={(e) => setFormData({ ...formData, quality_tag: e.target.value })}
                      >
                        {qualityTags.map(tag => (
                          <option key={tag} value={tag}>{tag}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Purchase Date *</label>
                    <input
                      type="date"
                      data-testid="holding-date-input"
                      className="input-field w-full"
                      value={formData.purchase_date}
                      onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Bank Name</label>
                    <input
                      type="text"
                      data-testid="holding-bank-input"
                      className="input-field w-full"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      data-testid="grandfathered-checkbox"
                      id="grandfathered"
                      checked={formData.grandfathered}
                      onChange={(e) => setFormData({ ...formData, grandfathered: e.target.checked })}
                    />
                    <label htmlFor="grandfathered" className="text-sm font-medium">Grandfathered</label>
                  </div>
                  {formData.grandfathered && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-2">Grandfathered Price</label>
                        <input
                          type="number"
                          data-testid="grandfathered-price-input"
                          step="0.01"
                          className="input-field w-full"
                          value={formData.grandfathered_price}
                          onChange={(e) => setFormData({ ...formData, grandfathered_price: parseFloat(e.target.value) })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Grandfathered Date</label>
                        <input
                          type="date"
                          data-testid="grandfathered-date-input"
                          className="input-field w-full"
                          value={formData.grandfathered_date}
                          onChange={(e) => setFormData({ ...formData, grandfathered_date: e.target.value })}
                        />
                      </div>
                    </>
                  )}
                  <Button type="submit" data-testid="submit-holding-button" className="btn-primary w-full">
                    Add Holding
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {editMode && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
            ⚠️ Edit Mode is ON - You can now edit or delete holdings. Click Exit Edit Mode when done.
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card" data-testid="total-value-card">
            <p className="text-sm text-gray-500 uppercase tracking-wider mb-1">Total Value</p>
            <p className="text-3xl font-bold text-primary">₹{totalValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
          </div>
          <div className="card" data-testid="pledged-value-card">
            <p className="text-sm text-gray-500 uppercase tracking-wider mb-1">Pledged</p>
            <p className="text-3xl font-bold text-[#EA580C]">₹{pledgedValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
          </div>
          <div className="card" data-testid="free-value-card">
            <p className="text-sm text-gray-500 uppercase tracking-wider mb-1">Free</p>
            <p className="text-3xl font-bold text-gray-700">₹{(totalValue - pledgedValue).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
          </div>
        </div>

        {/* Filter Controls */}
        <div className="card mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter size={18} className="text-gray-500" />
                <span className="text-sm font-medium">Filter by Quality:</span>
              </div>
              <div className="flex gap-2">
                {availableTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => setFilterTag(tag)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      filterTag === tag 
                        ? 'bg-primary text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {tag === 'all' ? 'All' : tag}
                  </button>
                ))}
              </div>
            </div>
            <div className="text-sm text-gray-600">
              Showing {displayHoldings.length} of {holdings.length} holdings
            </div>
          </div>
        </div>

        {/* Holdings Table */}
        <div className="card overflow-x-auto">
          {displayHoldings.length === 0 ? (
            <div className="text-center py-12" data-testid="empty-holdings">
              <p className="text-gray-500">
                {holdings.length === 0 
                  ? 'No holdings yet. Add your first holding to get started.'
                  : 'No holdings match the selected filter.'}
              </p>
            </div>
          ) : (
            <table className="w-full" data-testid="holdings-table">
              <thead>
                <tr className="border-b bg-gray-50">
                  {renderSortHeader('symbol', 'Symbol')}
                  {renderSortHeader('quantity', 'Qty')}
                  {renderSortHeader('type', 'Type')}
                  {renderSortHeader('tag', 'Quality')}
                  {renderSortHeader('price', 'Purchase')}
                  <th className="text-left py-3 px-4 font-semibold text-sm uppercase tracking-wider text-gray-600">Current</th>
                  {renderSortHeader('value', 'Value')}
                  {renderSortHeader('status', 'Status')}
                  {editMode && (
                    <th className="text-center py-3 px-4 font-semibold text-sm uppercase tracking-wider text-gray-600">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {displayHoldings.map((holding) => {
                  const currentPrice = prices[holding.symbol] || holding.purchase_price;
                  const value = currentPrice * holding.quantity;
                  const qualityTag = holding.custom_fields?.category || 'Unknown';
                  
                  if (editingId === holding.id) {
                    return (
                      <tr key={holding.id} className="border-b bg-gray-50">
                        <td className="py-3 px-4" colSpan={editMode ? 9 : 8}>
                          <div className="flex items-center gap-4">
                            <input
                              type="text"
                              className="input-field w-24"
                              value={editFormData.symbol}
                              onChange={(e) => setEditFormData({ ...editFormData, symbol: e.target.value.toUpperCase() })}
                            />
                            <input
                              type="number"
                              className="input-field w-20"
                              value={editFormData.quantity}
                              onChange={(e) => setEditFormData({ ...editFormData, quantity: parseInt(e.target.value) })}
                            />
                            <select
                              className="input-field w-28"
                              value={editFormData.purchase_type}
                              onChange={(e) => setEditFormData({ ...editFormData, purchase_type: e.target.value })}
                            >
                              <option value="Legacy">Legacy</option>
                              <option value="Primary">Primary</option>
                              <option value="MTF">MTF</option>
                            </select>
                            <select
                              className="input-field w-28"
                              value={editFormData.quality_tag}
                              onChange={(e) => setEditFormData({ ...editFormData, quality_tag: e.target.value })}
                            >
                              {qualityTags.map(tag => (
                                <option key={tag} value={tag}>{tag}</option>
                              ))}
                            </select>
                            <input
                              type="number"
                              step="0.01"
                              className="input-field w-24"
                              value={editFormData.purchase_price}
                              onChange={(e) => setEditFormData({ ...editFormData, purchase_price: parseFloat(e.target.value) })}
                            />
                            <Button onClick={() => handleSaveEdit(holding.id)} className="btn-primary px-3 py-1">
                              <Check size={16} />
                            </Button>
                            <Button onClick={handleCancelEdit} className="btn-outline px-3 py-1">
                              <X size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  
                  return (
                    <tr key={holding.id} className="border-b hover:bg-gray-50" data-testid={`holding-row-${holding.id}`}>
                      <td className="py-3 px-4">
                        <div>
                          <span className="font-semibold">{holding.symbol}</span>
                          {holding.grandfathered && (
                            <span className="ml-2 badge badge-success text-xs">GF</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">{holding.quantity.toLocaleString()}</td>
                      <td className="py-3 px-4">
                        <span className="badge badge-success">{holding.purchase_type}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getQualityTagColor(qualityTag)}`}>
                          {qualityTag}
                        </span>
                      </td>
                      <td className="py-3 px-4">₹{holding.purchase_price.toFixed(2)}</td>
                      <td className="py-3 px-4">₹{currentPrice.toFixed(2)}</td>
                      <td className="py-3 px-4 font-semibold">₹{value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                      <td className="py-3 px-4">
                        {holding.pledged ? (
                          <span className="badge badge-warning">Pledged</span>
                        ) : (
                          <span className="badge badge-success">Free</span>
                        )}
                      </td>
                      {editMode && (
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleEdit(holding)}
                              className="p-2 hover:bg-blue-50 rounded-lg text-blue-600"
                              title="Edit"
                            >
                              <Edit2 size={18} />
                            </button>
                            <button
                              data-testid={`toggle-pledge-${holding.id}`}
                              onClick={() => togglePledge(holding.id, holding.pledged)}
                              className="p-2 hover:bg-blue-50 rounded-lg text-blue-600"
                              title={holding.pledged ? 'Unpledge' : 'Pledge'}
                            >
                              {holding.pledged ? <ShieldOff size={18} /> : <Shield size={18} />}
                            </button>
                            <button
                              data-testid={`delete-holding-${holding.id}`}
                              onClick={() => handleDelete(holding.id, holding.symbol)}
                              className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                              title="Delete"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Confirmation Dialog */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="text-amber-500" size={24} />
                Confirm Action
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-gray-700 whitespace-pre-line">{confirmAction.message}</p>
            </div>
            <DialogFooter className="flex gap-3 justify-end">
              <Button 
                onClick={() => setShowConfirmDialog(false)} 
                className="btn-outline"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleConfirm} 
                className={confirmAction.type === 'delete' ? 'bg-red-600 hover:bg-red-700 text-white' : 'btn-primary'}
              >
                {confirmAction.type === 'rollback' ? 'Yes, Rollback' : 'Yes, Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Portfolio;
